// A theme someone shared by link. The link carries the theme itself, checked
// like any other: colours and a scene, never code. The board behind wears it
// while this is open, so the choice is made looking at the real thing.

import { useEffect, useState } from "react";
import { Check, Palette } from "lucide-react";
import { Dialog } from "./ui";
import { previewOf, readShared, saveTheme } from "../state/themes";
import { set, toast, useStore } from "../state/store";
import { setPreviewTheme } from "../themes/themes";
import { ThemeCard } from "../themes/ThemeCard";

export function InstallTheme() {
  const share = useStore((s) => s.sharedTheme);
  const [busy, setBusy] = useState(false);

  // a link opened straight onto the board
  useEffect(() => {
    const h = location.hash;
    if (!/[#&]theme=/.test(h)) return;
    // the link leaves the address first, so nothing in it comes back with a reload
    history.replaceState(null, "", location.pathname + location.search);
    const found = readShared(h);
    if (found) set({ sharedTheme: found });
    else toast({ level: "warn", title: "That theme link did not open", body: "It may have been cut short when it was copied." });
  }, []);

  useEffect(() => {
    if (!share) return;
    setPreviewTheme(previewOf(share));
    set({ themePreview: "preview:shared" });
    return () => {
      setPreviewTheme(null);
      set({ themePreview: null });
    };
  }, [share]);

  if (!share) return null;
  const close = () => set({ sharedTheme: null });
  const add = async (use: boolean) => {
    setBusy(true);
    try {
      const t = await saveTheme(share, { apply: use, by: share.by ?? "someone else" });
      toast({ level: "success", title: use ? `Now using “${t.name}”` : `Saved “${t.name}”`, body: use ? undefined : "It is in the Look panel, under Yours." });
      close();
    } catch (e) {
      toast({ level: "error", title: "Could not add that theme", body: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={close}
      width={460}
      className="install-theme"
      title={share.name}
      icon={<Palette size={16} />}
      footer={
        <>
          <button className="btn btn-ghost" onClick={close} disabled={busy}>
            No thanks
          </button>
          <button className="btn" onClick={() => void add(false)} disabled={busy}>
            Save for later
          </button>
          <button className="btn btn-primary" onClick={() => void add(true)} disabled={busy} autoFocus>
            {busy ? <span className="spinner" /> : <Check size={14} />} Use this theme
          </button>
        </>
      }
    >
      <p className="dialog-lede">
        {share.blurb || "A theme someone shared with you."}
        {share.by ? ` Made by ${share.by}.` : ""}
      </p>
      <div className="install-still">
        <ThemeCard id="preview:shared" big on={false} onPick={() => void add(true)} />
      </div>
      <p className="hub-note">You're seeing it on your board now. A theme is colors and a scene, never code, so adding it changes how your board looks and nothing else.</p>
    </Dialog>
  );
}
